
import React, { useState, useEffect } from 'react';
import { useActivityManagement } from '@/hooks/useActivityManagement';
import { usePracticeSession } from '@/hooks/usePracticeSession';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Play, Pause, SkipForward, Clock, CheckCircle } from 'lucide-react';
import { Program, Activity } from '@/types/program';

interface PracticeRunnerProps {
  program: Program;
  onBack: () => void;
}

export const PracticeRunner = ({ program, onBack }: PracticeRunnerProps) => {
  const { activities, loading } = useActivityManagement(program.id);
  const { createSession } = usePracticeSession(program.team_id);
  
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [sessionNotes, setSessionNotes] = useState('');
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sessionCompleted, setSessionCompleted] = useState(false);

  const currentActivity = activities[currentActivityIndex];
  const totalActivities = activities.length;
  const progress = totalActivities > 0 ? ((currentActivityIndex + 1) / totalActivities) * 100 : 0;

  useEffect(() => {
    if (currentActivity && !sessionStarted) {
      setTimeRemaining(currentActivity.duration * 60); // Convert minutes to seconds
    }
  }, [currentActivity, sessionStarted]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isRunning && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setIsRunning(false);
            // Auto advance to next activity
            if (currentActivityIndex < totalActivities - 1) {
              setTimeout(() => {
                setCurrentActivityIndex(prev => prev + 1);
                setTimeRemaining(activities[currentActivityIndex + 1]?.duration * 60 || 0);
              }, 1000);
            } else {
              // Session completed
              setSessionCompleted(true);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isRunning, timeRemaining, currentActivityIndex, totalActivities, activities]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartSession = async () => {
    try {
      await createSession({
        program_id: program.id,
        team_id: program.team_id,
        session_date: new Date().toISOString().split('T')[0],
        start_time: new Date().toISOString(),
        status: 'in_progress'
      });
      setSessionStarted(true);
      setIsRunning(true);
    } catch (error) {
      console.error('Failed to start session:', error);
    }
  };

  const handlePauseResume = () => {
    setIsRunning(!isRunning);
  };

  const handleNextActivity = () => {
    if (currentActivityIndex < totalActivities - 1) {
      setCurrentActivityIndex(prev => prev + 1);
      setTimeRemaining(activities[currentActivityIndex + 1]?.duration * 60 || 0);
      setIsRunning(false);
    }
  };

  const handleCompleteSession = () => {
    setSessionCompleted(true);
    setIsRunning(false);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'warm_up':
        return 'bg-orange-100 text-orange-800';
      case 'drill':
        return 'bg-blue-100 text-blue-800';
      case 'conditioning':
        return 'bg-red-100 text-red-800';
      case 'scrimmage':
        return 'bg-green-100 text-green-800';
      case 'cool_down':
        return 'bg-purple-100 text-purple-800';
      case 'strategy':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'warm_up':
        return 'Warm Up';
      case 'cool_down':
        return 'Cool Down';
      default:
        return category.charAt(0).toUpperCase() + category.slice(1);
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading practice session...</div>;
  }

  if (activities.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Programs
          </Button>
          <h3 className="text-lg font-semibold">{program.title}</h3>
        </div>
        <Card>
          <CardContent className="text-center py-8">
            <h4 className="text-lg font-semibold mb-2">No Activities</h4>
            <p className="text-muted-foreground">
              This program doesn't have any activities yet. Add some activities before running a practice session.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Programs
          </Button>
          <div>
            <h3 className="text-lg font-semibold">{program.title}</h3>
            <p className="text-sm text-muted-foreground">
              Practice Session Runner
            </p>
          </div>
        </div>
        {sessionCompleted && (
          <Badge variant="default" className="bg-green-600">
            <CheckCircle className="h-4 w-4 mr-1" />
            Session Completed
          </Badge>
        )}
      </div>

      {/* Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Session Progress</span>
            <span className="text-sm font-normal">
              Activity {currentActivityIndex + 1} of {totalActivities}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Progress value={progress} className="mb-2" />
          <p className="text-sm text-muted-foreground">
            {Math.round(progress)}% complete
          </p>
        </CardContent>
      </Card>

      {/* Current Activity */}
      {currentActivity && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Current Activity</span>
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4" />
                <span className="text-xl font-mono">
                  {formatTime(timeRemaining)}
                </span>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="text-lg font-semibold">{currentActivity.title}</h4>
              {currentActivity.description && (
                <p className="text-muted-foreground mt-1">{currentActivity.description}</p>
              )}
              <div className="flex items-center space-x-2 mt-2">
                <Badge className={getCategoryColor(currentActivity.category)}>
                  {getCategoryLabel(currentActivity.category)}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {currentActivity.duration} minutes
                </span>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-4">
              {!sessionStarted ? (
                <Button onClick={handleStartSession} size="lg">
                  <Play className="h-5 w-5 mr-2" />
                  Start Practice Session
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handlePauseResume}
                    variant={isRunning ? "secondary" : "default"}
                    size="lg"
                  >
                    {isRunning ? (
                      <>
                        <Pause className="h-5 w-5 mr-2" />
                        Pause
                      </>
                    ) : (
                      <>
                        <Play className="h-5 w-5 mr-2" />
                        Resume
                      </>
                    )}
                  </Button>
                  
                  {currentActivityIndex < totalActivities - 1 ? (
                    <Button onClick={handleNextActivity} variant="outline" size="lg">
                      <SkipForward className="h-5 w-5 mr-2" />
                      Next Activity
                    </Button>
                  ) : (
                    <Button onClick={handleCompleteSession} variant="outline" size="lg">
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Complete Session
                    </Button>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Session Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Session Notes</CardTitle>
          <CardDescription>
            Add notes about this practice session
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={sessionNotes}
            onChange={(e) => setSessionNotes(e.target.value)}
            placeholder="Add your notes about the practice session..."
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Activity List */}
      <Card>
        <CardHeader>
          <CardTitle>Program Activities</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {activities.map((activity, index) => (
              <div
                key={activity.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  index === currentActivityIndex 
                    ? 'bg-primary/10 border-primary' 
                    : index < currentActivityIndex 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
                    index === currentActivityIndex 
                      ? 'bg-primary text-primary-foreground' 
                      : index < currentActivityIndex 
                        ? 'bg-green-600 text-white' 
                        : 'bg-gray-300 text-gray-600'
                  }`}>
                    {index < currentActivityIndex ? '✓' : index + 1}
                  </div>
                  <div>
                    <h5 className="font-medium">{activity.title}</h5>
                    <div className="flex items-center space-x-2">
                      <Badge className={getCategoryColor(activity.category)}>
                        {getCategoryLabel(activity.category)}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {activity.duration} min
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
